import React, { useRef, useEffect, useState } from 'react';
import { Form, Button, Row, Col, Table, Tabs, Tab, Modal } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useForm, useWatch } from 'react-hook-form';
import SignatureCanvas from 'react-signature-canvas';
import { SUBMIT_FORM, VIEW_FORM } from 'actions/formActions';
import { toast } from 'react-toastify';
import _ from 'lodash';

const LTCableForm = () => {
  const dispatch = useDispatch();
  const { viewFormData } = useSelector((state) => state.checklist);
  const { formId, stationId } = useParams();
  const sigCanvas = useRef(null);
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [currentSignature, setCurrentSignature] = useState({ level: '', role: '' });
  const [isLoading, setIsLoading] = useState(true);
  const [isEditable, setIsEditable] = useState(true);

  const { register, control, handleSubmit, setValue, getValues } = useForm({
    defaultValues: {
      station_id: stationId || '',
      project_name: '',
      equipment: '',
      manufacturer: '',
      output: '',
      visual_checks: [
        { description: 'Have the correct methods of cable termination and tagging been checked?', status: '', remark: '' },
        { description: 'Has the cable TC been checked?', status: '', remark: '' },
      ],
      physical_checks: [
        { description: 'Are the busbar joints properly connected and tightened?', status: '', remark: '' },
        { description: 'Has the proper size lugs been used with the correct lugging tool?', status: '', remark: '' },
      ],
      l1_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l1_other_comments: '',
      l1_status: 'draft',
      all_cables_healthy: '',
      testing_commissioning: '',
      cables: Array(8).fill({
        cable_number: 0,
        status: '',
        tag_no: '',
        cable_size: '',
        from: '',
        to: '',
        cable_length: '',
        r_y_continuity: '',
        y_b_continuity: '',
        b_r_continuity: '',
        r_n_continuity: '',
        y_n_continuity: '',
        b_n_continuity: '',
        r_e_continuity: '',
        y_e_continuity: '',
        b_e_continuity: '',
        n_e_continuity: '',
        r_y_ir_value: '',
        y_b_ir_value: '',
        b_r_ir_value: '',
        r_n_ir_value: '',
        y_n_ir_value: '',
        b_n_ir_value: '',
        r_e_ir_value: '',
        y_e_ir_value: '',
        b_e_ir_value: '',
        n_e_ir_value: '',
        healthy: '',
        extra_length_side: '',
      }).map((item, index) => ({ ...item, cable_number: index + 1 })),
      instruments: [
        { description: 'IR Tester', serial: '', range: '', calibration: '' },
        { description: 'Digital Multi meter / Tong Tester', serial: '', range: '', calibration: '' },
      ],
      l2_comments: {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      },
      l2_other_comments: '',
      l2_observations: '',
      l2_status: 'draft',
    },
  });

  const formValues = useWatch({ control });

  const autoSave = React.useCallback(
    _.debounce((data) => {
      if (!isEditable) return;
      const common_status = data.l1_status === 'pending' && data.l2_status === 'pending' ? 'pending' : 'draft';
      const payload = {
        form_data: {
          status: common_status,
          form_id: formId,
          formName: 'lt_cabel',
          station_id: data.station_id,
          project_name: data.project_name,
          equipment: data.equipment,
          manufacturer: data.manufacturer,
          output: data.output,
          l1: {
            visual_checks: data.visual_checks,
            physical_checks: data.physical_checks,
            comments: data.l1_comments,
            other_comments: data.l1_other_comments,
            status: data.l1_status,
          },
          l2: {
            all_cables_healthy: data.all_cables_healthy,
            testing_commissioning: data.testing_commissioning,
            cables: data.cables,
            instruments: data.instruments,
            comments: data.l2_comments,
            other_comments: data.l2_other_comments,
            observations: data.l2_observations,
            status: data.l2_status,
          },
        },
      };
      dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    }, 2000),
    [dispatch, formId]
  );

  useEffect(() => {
    autoSave(formValues);
  }, [formValues]);

  useEffect(() => {
    if (formId) {
      dispatch({ type: VIEW_FORM, payload: { formId, formName: 'lt_cabel' } });
    }
    setIsLoading(false);
  }, [dispatch, formId]);

  useEffect(() => {
    if (viewFormData) {
      const data = viewFormData || {};
      setIsEditable(!['pending', 'approved'].includes(data.status));
      setValue('station_id', data.station_id || stationId || '');
      setValue('project_name', data.project_name || '');
      setValue('equipment', data.equipment || '');
      setValue('manufacturer', data.manufacturer || '');
      setValue('output', data.output || '');
      setValue('visual_checks', Array.isArray(data.l1?.visual_checks)
        ? data.l1.visual_checks
        : [
          { description: 'Have the correct methods of cable termination and tagging been checked?', status: '', remark: '' },
          { description: 'Has the cable TC been checked?', status: '', remark: '' },
        ]);
      setValue('physical_checks', Array.isArray(data.l1?.physical_checks)
        ? data.l1.physical_checks
        : [
          { description: 'Are the busbar joints properly connected and tightened?', status: '', remark: '' },
          { description: 'Has the proper size lugs been used with the correct lugging tool?', status: '', remark: '' },
        ]);
      setValue('l1_comments', data.l1?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l1_other_comments', data.l1?.other_comments || '');
      setValue('l1_status', data.l1?.status || 'draft');
      setValue('all_cables_healthy', data.l2?.all_cables_healthy || '');
      setValue('testing_commissioning', data.l2?.testing_commissioning || '');
      setValue('cables', Array.isArray(data.l2?.cables)
        ? data.l2.cables
        : Array(8).fill({
          cable_number: 0,
          status: '',
          tag_no: '',
          cable_size: '',
          from: '',
          to: '',
          cable_length: '',
          r_y_continuity: '',
          y_b_continuity: '',
          b_r_continuity: '',
          r_n_continuity: '',
          y_n_continuity: '',
          b_n_continuity: '',
          r_e_continuity: '',
          y_e_continuity: '',
          b_e_continuity: '',
          n_e_continuity: '',
          r_y_ir_value: '',
          y_b_ir_value: '',
          b_r_ir_value: '',
          r_n_ir_value: '',
          y_n_ir_value: '',
          b_n_ir_value: '',
          r_e_ir_value: '',
          y_e_ir_value: '',
          b_e_ir_value: '',
          n_e_ir_value: '',
          healthy: '',
          extra_length_side: '',
        }).map((item, index) => ({ ...item, cable_number: index + 1 })));
      setValue('instruments', Array.isArray(data.l2?.instruments)
        ? data.l2.instruments
        : [
          { description: 'IR Tester', serial: '', range: '', calibration: '' },
          { description: 'Digital Multi meter / Tong Tester', serial: '', range: '', calibration: '' },
        ]);
      setValue('l2_comments', data.l2?.comments || {
        contractor: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        charge_engineer: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        quality: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
        optional: { name: '', company: '', signature: '', signatureType: 'upload', date: '' },
      });
      setValue('l2_other_comments', data.l2?.other_comments || '');
      setValue('l2_observations', data.l2?.observations || '');
      setValue('l2_status', data.l2?.status || 'draft');
    }
  }, [viewFormData, stationId, setValue]);

  const handleImageUpload = (e, level, role) => {
    if (!isEditable) return;
    const file = e.target.files[0];
    const maxSizeInBytes = 0.05 * 1024 * 1024; // 50KB
    if (file) {
      if (file.size > maxSizeInBytes) {
        toast.error('File size exceeds 50KB. Please upload a smaller image.');
        handleResetSignature(level, role);
        sigCanvas.current?.clear();
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => {
        setValue(`${level}_comments.${role}.signature`, reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSignatureTypeChange = (level, role, type) => {
    if (!isEditable) return;
    setValue(`${level}_comments.${role}.signatureType`, type);
    setValue(`${level}_comments.${role}.signature`, '');
    if (type === 'digital') {
      setCurrentSignature({ level, role });
      setShowModal(true);
    }
  };

  const handleResetSignature = (level, role) => {
    if (!isEditable) return;
    setValue(`${level}_comments.${role}.signature`, '');
    setValue(`${level}_comments.${role}.signatureType`, 'upload');
  };

  const handleSaveSignature = () => {
    if (!isEditable) return;
    if (sigCanvas.current && !sigCanvas.current.isEmpty()) {
      const signatureData = sigCanvas.current.toDataURL();
      setValue(`${currentSignature.level}_comments.${currentSignature.role}.signature`, signatureData);
    }
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const handleClearSignature = () => {
    if (!isEditable) return;
    sigCanvas.current?.clear();
  };

  const handleCloseModal = () => {
    if (!isEditable) {
      setShowModal(false);
      return;
    }
    handleResetSignature(currentSignature.level, currentSignature.role);
    setShowModal(false);
    sigCanvas.current?.clear();
  };

  const onSubmit = (level, status) => (data) => {
    if (!isEditable) {
      toast.info('Form is not editable in pending or approved status');
      return;
    }
    const l1_status = level === 'l1' ? status : data.l1_status;
    const l2_status = level === 'l2' ? status : data.l2_status;
    const common_status = l1_status === 'pending' && l2_status === 'pending' ? 'pending' : 'draft';
    const payload = {
      form_data: {
        status: common_status,
        form_id: formId,
        formName: 'lt_cabel',
        station_id: data.station_id,
        project_name: data.project_name,
        equipment: data.equipment,
        manufacturer: data.manufacturer,
        output: data.output,
        l1: {
          visual_checks: data.visual_checks,
          physical_checks: data.physical_checks,
          comments: data.l1_comments,
          other_comments: data.l1_other_comments,
          status: l1_status,
        },
        l2: {
          all_cables_healthy: data.all_cables_healthy,
          testing_commissioning: data.testing_commissioning,
          cables: data.cables,
          instruments: data.instruments,
          comments: data.l2_comments,
          other_comments: data.l2_other_comments,
          observations: data.l2_observations,
          status: l2_status,
        },
      },
    };
    dispatch({ type: SUBMIT_FORM, payload: { data: payload.form_data } });
    toast.success(`Level ${level.toUpperCase()} ${status === 'pending' ? 'Submitted' : 'Saved as Draft'} Successfully`);
    navigate(-1);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page-body container-xl">
      <h2 className="page-title mb-4">LT Cable - Site Acceptance Testing</h2>
      {!isEditable && (
        <div className="alert alert-warning mb-4">
          This form is in View Mode Only.
        </div>
      )}
      <Form>
        <h3>1. Equipment Details</h3>
        <Row>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Station ID</Form.Label>
              <Form.Control type="text" {...register('station_id')} readOnly />
            </Form.Group>
          </Col>
          <Col md={4}>
            <Form.Group className="mb-3">
              <Form.Label>Project Name</Form.Label>
              <Form.Control type="text" {...register('project_name')} disabled={!isEditable} />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group className="mb-3">
              <Form.Label>Equipment Manufacturer</Form.Label>
              <Form.Control type="text" {...register('equipment')} disabled={!isEditable} />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group className="mb-3">
              <Form.Label>Output</Form.Label>
              <Form.Control type="text" {...register('output')} disabled={!isEditable} />
            </Form.Group>
          </Col>
        </Row>

        <Tabs defaultActiveKey="level1" id="sat-tabs" className="mb-3">
          <Tab eventKey="level1" title="Level 1 - Pre-Commissioning">
            <h3 className="mt-4">2. Visual Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Have the correct methods of cable termination and tagging been checked?',
                  'Has the cable TC been checked?',
                ].map((description, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{description}</td>
                    <td>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        name={`visual_checks_${index}`}
                        value="Yes"
                        {...register(`visual_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        name={`visual_checks_${index}`}
                        value="No"
                        {...register(`visual_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                      <Form.Check
                        inline
                        label="NA"
                        type="radio"
                        name={`visual_checks_${index}`}
                        value="NA"
                        {...register(`visual_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`visual_checks.${index}.remark`)}
                        disabled={!isEditable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">3. Physical Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Description</th>
                  <th>Status</th>
                  <th>Remark</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Are the busbar joints properly connected and tightened?',
                  'Has the proper size lugs been used with the correct lugging tool?',
                ].map((description, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>{description}</td>
                    <td>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        name={`physical_checks_${index}`}
                        value="Yes"
                        {...register(`physical_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        name={`physical_checks_${index}`}
                        value="No"
                        {...register(`physical_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                      <Form.Check
                        inline
                        label="NA"
                        type="radio"
                        name={`physical_checks_${index}`}
                        value="NA"
                        {...register(`physical_checks.${index}.status`)}
                        disabled={!isEditable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`physical_checks.${index}.remark`)}
                        disabled={!isEditable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">4. Level 1 Pre-Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((role) => (
                      <td key={role}>
                        {field === 'Date' ? (
                          <Form.Control
                            type="date"
                            {...register(`l1_comments.${role}.${field.toLowerCase()}`)}
                            disabled={!isEditable}
                          />
                        ) : field === 'Signature' ? (
                          <div>
                            <Form.Select
                              {...register(`l1_comments.${role}.signatureType`)}
                              onChange={(e) => handleSignatureTypeChange('l1', role, e.target.value)}
                              className="mb-2"
                              disabled={!isEditable}
                            >
                              <option value="upload">Image Upload</option>
                              <option value="digital">Digital Sign</option>
                            </Form.Select>
                            {getValues(`l1_comments.${role}.signatureType`) === 'upload' && (
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, 'l1', role)}
                                className="mb-2"
                                disabled={!isEditable}
                              />
                            )}
                            {getValues(`l1_comments.${role}.signature`) && (
                              <div>
                                <img
                                  src={getValues(`l1_comments.${role}.signature`)}
                                  alt="Signature"
                                  style={{ maxWidth: '100px', marginTop: '5px' }}
                                />
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleResetSignature('l1', role)}
                                  className="mt-2"
                                  disabled={!isEditable}
                                >
                                  Reset Signature
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <Form.Control
                            type="text"
                            {...register(`l1_comments.${role}.${field.toLowerCase()}`)}
                            disabled={!isEditable}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">5. Other Comments/Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                {...register('l1_other_comments')}
                disabled={!isEditable}
                placeholder="Enter any additional comments or remarks for Level 1"
              />
            </Form.Group>

            <Button
              variant="success"
              onClick={handleSubmit(onSubmit('l1', 'pending'))}
              className="mt-4"
              disabled={!isEditable}
            >
              Submit Level 1
            </Button>
            <Button
              variant="secondary"
              style={{ marginLeft: '10px' }}
              onClick={handleSubmit(onSubmit('l1', 'draft'))}
              className="mt-4"
              disabled={!isEditable}
            >
              Save As Draft
            </Button>
          </Tab>

          <Tab eventKey="level2" title="Level 2 - Commissioning">
            <h3 className="mt-4">Note: Ensure all below points are in co-ordinate with ANNEXURE: LT CABEL: COMMISSIONINGPART: Level 2 and if all tests are found correct select Yes / No.</h3>
            <h3 className="mt-4">6. Commissioning Part</h3>
            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>All LT Cables Found Healthy & Ready to Charge</Form.Label>
                  <Form.Select {...register('all_cables_healthy')} disabled={!isEditable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Label>Testing & Commissioning</Form.Label>
                  <Form.Select {...register('testing_commissioning')} disabled={!isEditable}>
                    <option value="">Select</option>
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </Form.Select>
                </Form.Group>
              </Col>
            </Row>

            <h3 className="mt-4">7. Individual Cable Checks</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Description</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  'Cable -1 Is found Healthy and ready to charge',
                  'Cable -2 Is found Healthy and ready to charge',
                  'Cable -3 Is found Healthy and ready to charge',
                  'Cable -4 Is found Healthy and ready to charge',
                  'Cable -5 Is found Healthy and ready to charge',
                  'Cable -6 Is found Healthy and ready to charge',
                  'Cable -7 Is found Healthy and ready to charge',
                  'Cable -8 Is found Healthy and ready to charge',
                ].map((description, index) => (
                  <tr key={index}>
                    <td>{description}</td>
                    <td>
                      <Form.Check
                        inline
                        label="Yes"
                        type="radio"
                        name={`cables_${index}_status`}
                        value="Yes"
                        {...register(`cables.${index}.status`)}
                        disabled={!isEditable}
                      />
                      <Form.Check
                        inline
                        label="No"
                        type="radio"
                        name={`cables_${index}_status`}
                        value="No"
                        {...register(`cables.${index}.status`)}
                        disabled={!isEditable}
                      />
                      <Form.Check
                        inline
                        label="NA"
                        type="radio"
                        name={`cables_${index}_status`}
                        value="NA"
                        {...register(`cables.${index}.status`)}
                        disabled={!isEditable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">8. Test Instruments Details and Calibration Status</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th>Sr. No.</th>
                  <th>Instrument Description</th>
                  <th>Instrument Serial No.</th>
                  <th>Range</th>
                  <th>Calibration Status</th>
                </tr>
              </thead>
              <tbody>
                {[
                  { description: 'IR Tester', serial: '', range: '', calibration: '' },
                  { description: 'Digital Multi meter / Tong Tester', serial: '', range: '', calibration: '' },
                ].map((instrument, index) => (
                  <tr key={index}>
                    <td>{index + 1}</td>
                    <td>
                      <Form.Control
                        type="text"
                        value={instrument.description}
                        disabled
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`instruments.${index}.serial`)}
                        disabled={!isEditable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`instruments.${index}.range`)}
                        disabled={!isEditable}
                      />
                    </td>
                    <td>
                      <Form.Control
                        type="text"
                        {...register(`instruments.${index}.calibration`)}
                        disabled={!isEditable}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">TESTING & COMMISSIONING Electrical Checks / Tests</h3>
            <h3 className="mt-4">9. Cable Details</h3>

            {Array.from({ length: 8 }, (_, cableIndex) => (
              <div key={cableIndex} className="mb-4">
                <h4>Cable {cableIndex + 1}</h4>
                <Row>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Tag No</Form.Label>
                      <Form.Control
                        type="text"
                        {...register(`cables.${cableIndex}.tag_no`)}
                        disabled={!isEditable}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cable Size</Form.Label>
                      <Form.Control
                        type="text"
                        {...register(`cables.${cableIndex}.cable_size`)}
                        disabled={!isEditable}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>From</Form.Label>
                      <Form.Control
                        type="text"
                        {...register(`cables.${cableIndex}.from`)}
                        disabled={!isEditable}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>To</Form.Label>
                      <Form.Control
                        type="text"
                        {...register(`cables.${cableIndex}.to`)}
                        disabled={!isEditable}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={2}>
                    <Form.Group className="mb-3">
                      <Form.Label>Cable Length (M)</Form.Label>
                      <Form.Control
                        type="text"
                        {...register(`cables.${cableIndex}.cable_length`)}
                        disabled={!isEditable}
                      />
                    </Form.Group>
                  </Col>
                </Row>
                <Table bordered responsive>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <td>R-Y</td>
                      <td>Y-B</td>
                      <td>B-R</td>
                      <td>R-N</td>
                      <td>Y-N</td>
                      <td>B-N</td>
                      <td>R-E</td>
                      <td>Y-E</td>
                      <td>B-E</td>
                      <td>N-E</td>
                    </tr>
                    <tr>
                      <th>Continuity</th>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.r_y_continuity`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.y_b_continuity`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.b_r_continuity`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.r_n_continuity`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.y_n_continuity`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.b_n_continuity`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.r_e_continuity`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.y_e_continuity`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.b_e_continuity`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.n_e_continuity`)}
                          disabled={!isEditable}
                        />
                      </td>
                    </tr>
                    <tr>
                      <th>IR Values</th>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.r_y_ir_value`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.y_b_ir_value`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.b_r_ir_value`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.r_n_ir_value`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.y_n_ir_value`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.b_n_ir_value`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.r_e_ir_value`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.y_e_ir_value`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.b_e_ir_value`)}
                          disabled={!isEditable}
                        />
                      </td>
                      <td>
                        <Form.Control
                          type="text"
                          {...register(`cables.${cableIndex}.n_e_ir_value`)}
                          disabled={!isEditable}
                        />
                      </td>
                    </tr>
                  </thead>
                  <tbody></tbody>
                </Table>
              </div>
            ))}

            <h3 className="mt-4">10. Observations During Commissioning</h3>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                {...register('l2_observations')}
                disabled={!isEditable}
                placeholder="Enter observations during commissioning"
              />
            </Form.Group>

            <h3 className="mt-4">11. Level 2 Commissioning Comments</h3>
            <Table bordered>
              <thead>
                <tr>
                  <th></th>
                  <th>Engineer - Contractor</th>
                  <th>Engineer - Charge Zone</th>
                  <th>Quality - Charge Zone</th>
                  <th>Optional</th>
                </tr>
              </thead>
              <tbody>
                {['Name', 'Company', 'Signature', 'Date'].map((field) => (
                  <tr key={field}>
                    <td>{field}</td>
                    {['contractor', 'charge_engineer', 'quality', 'optional'].map((role) => (
                      <td key={role}>
                        {field === 'Date' ? (
                          <Form.Control
                            type="date"
                            {...register(`l2_comments.${role}.${field.toLowerCase()}`)}
                            disabled={!isEditable}
                          />
                        ) : field === 'Signature' ? (
                          <div>
                            <Form.Select
                              {...register(`l2_comments.${role}.signatureType`)}
                              onChange={(e) => handleSignatureTypeChange('l2', role, e.target.value)}
                              className="mb-2"
                              disabled={!isEditable}
                            >
                              <option value="upload">Image Upload</option>
                              <option value="digital">Digital Sign</option>
                            </Form.Select>
                            {getValues(`l2_comments.${role}.signatureType`) === 'upload' && (
                              <Form.Control
                                type="file"
                                accept="image/*"
                                onChange={(e) => handleImageUpload(e, 'l2', role)}
                                className="mb-2"
                                disabled={!isEditable}
                              />
                            )}
                            {getValues(`l2_comments.${role}.signature`) && (
                              <div>
                                <img
                                  src={getValues(`l2_comments.${role}.signature`)}
                                  alt="Signature"
                                  style={{ maxWidth: '100px', marginTop: '5px' }}
                                />
                                <Button
                                  variant="danger"
                                  size="sm"
                                  onClick={() => handleResetSignature('l2', role)}
                                  className="mt-2"
                                  disabled={!isEditable}
                                >
                                  Reset Signature
                                </Button>
                              </div>
                            )}
                          </div>
                        ) : (
                          <Form.Control
                            type="text"
                            {...register(`l2_comments.${role}.${field.toLowerCase()}`)}
                            disabled={!isEditable}
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>

            <h3 className="mt-4">12. Other Comments/Remarks</h3>
            <Form.Group className="mb-3">
              <Form.Control
                as="textarea"
                rows={4}
                {...register('l2_other_comments')}
                disabled={!isEditable}
                placeholder="Enter any additional comments or remarks for Level 2"
              />
            </Form.Group>

            <Button
              variant="success"
              onClick={handleSubmit(onSubmit('l2', 'pending'))}
              className="mt-4"
              disabled={!isEditable}
            >
              Submit Level 2
            </Button>
            <Button
              variant="secondary"
              style={{ marginLeft: '10px' }}
              onClick={handleSubmit(onSubmit('l2', 'draft'))}
              className="mt-4"
              disabled={!isEditable}
            >
              Save As Draft
            </Button>
          </Tab>
        </Tabs>

        <Modal show={showModal && isEditable} onHide={handleCloseModal}>
          <Modal.Header closeButton>
            <Modal.Title>Draw Signature</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <SignatureCanvas
              ref={sigCanvas}
              penColor="black"
              canvasProps={{ width: 500, height: 200, className: 'sigCanvas border' }}
            />
            <div className="mt-2">
              <Button variant="secondary" onClick={handleClearSignature} className="me-2" disabled={!isEditable}>
                Clear
              </Button>
              <Button variant="primary" onClick={handleSaveSignature} disabled={!isEditable}>
                Save Signature
              </Button>
            </div>
          </Modal.Body>
        </Modal>
      </Form>
    </div>
  );
};

export default LTCableForm;